/**
 * Componente: Modal de Regalo — Prueba Gratuita 15 Días Plan Pro
 * Muestra el obsequio especial otorgado al completar el registro.
 */

'use client';

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import confetti from 'canvas-confetti';
import styles from './RegaloModal.module.css';

export interface RegaloModalProps {
  isOpen: boolean;
  nombreGranja?: string;
  onClose: () => void;
  onAceptar: () => void;
}

export const RegaloModal: React.FC<RegaloModalProps> = ({
  isOpen,
  nombreGranja,
  onClose,
  onAceptar,
}) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Disparo festivo de confeti al abrir el modal
  useEffect(() => {
    if (isOpen) {
      try {
        confetti({
          particleCount: 90,
          spread: 85,
          origin: { y: 0.5 },
          colors: ['#FFB800', '#70a32c', '#c25ee8', '#EE5847', '#FFFFFF'],
        });
      } catch {
        // Silencioso
      }
    }
  }, [isOpen]);

  // Cerrar con Escape
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !mounted) return null;

  return createPortal(
    <div
      className={styles.backdrop}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="regalo-modal-title"
    >
      <div
        className={styles.modalCard}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Botón Cerrar (X) */}
        <button
          type="button"
          onClick={onClose}
          className={styles.closeButton}
          aria-label="Cerrar modal de regalo"
        >
          <span className="material-symbols-outlined text-[20px]">close</span>
        </button>

        {/* Icono Superior de Regalo (Solo Icono sin Fondo) */}
        <div className={styles.giftIconOnly}>
          <span className="material-symbols-outlined text-[54px]">
            redeem
          </span>
        </div>

        {/* Título */}
        <h2 id="regalo-modal-title" className={styles.title}>
          ¡Tu Regalo: <span className={styles.titleHighlight}>15 Días Gratis</span>!
        </h2>

        {/* Subtítulo */}
        <p className={styles.subtitle}>
          {nombreGranja
            ? `Activado para impulsar tu granja "${nombreGranja}" con todas las herramientas profesionales.`
            : 'Activado para que disfrutes de todas las herramientas avanzadas sin costo.'}
        </p>

        {/* Tarjeta del Plan Pro */}
        <div className={styles.promoCard}>
          <div className={styles.promoHeader}>
            <div className={styles.planName}>
              <span className="material-symbols-outlined text-[#FFB800] text-[22px]">
                workspace_premium
              </span>
              <span>AviFácil Pro</span>
            </div>
            <span className={styles.planTag}>
              <span className="material-symbols-outlined text-[14px]">star</span>
              15 Días de Regalo
            </span>
          </div>

          <div className={styles.featureList}>
            <div className={styles.featureItem}>
              <div className={styles.featureIcon}>
                <span className="material-symbols-outlined text-[15px]">check</span>
              </div>
              <div className={styles.featureText}>
                <span className={styles.featureStrong}>Control total de producción:</span> Seguimiento diario de mortalidad, pesajes y conversión alimenticia.
              </div>
            </div>

            <div className={styles.featureItem}>
              <div className={styles.featureIcon}>
                <span className="material-symbols-outlined text-[15px]">check</span>
              </div>
              <div className={styles.featureText}>
                <span className={styles.featureStrong}>Asistente Inteligente Elie 24/7:</span> Alertas y recomendaciones automáticas directamente en tu WhatsApp.
              </div>
            </div>

            <div className={styles.featureItem}>
              <div className={styles.featureIcon}>
                <span className="material-symbols-outlined text-[15px]">check</span>
              </div>
              <div className={styles.featureText}>
                <span className={styles.featureStrong}>Reportes listos para descargar:</span> Balances financieros y hojas de registro preconfiguradas.
              </div>
            </div>

            <div className={styles.featureItem}>
              <div className={styles.featureIcon}>
                <span className="material-symbols-outlined text-[15px]">check</span>
              </div>
              <div className={styles.featureText}>
                <span className={styles.featureStrong}>Sin tarjeta ni compromisos:</span> Acceso completo sin cargos automáticos.
              </div>
            </div>
          </div>
        </div>

        {/* Botón CTA para ingresar a la granja */}
        <button
          type="button"
          onClick={onAceptar}
          className={styles.ctaButton}
        >
          <span>Comenzar a usar mi regalo</span>
          <span className="material-symbols-outlined text-[20px]">
            arrow_forward
          </span>
        </button>
      </div>
    </div>,
    document.body
  );
};
